import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import {
  Button,
  Col,
  Form,
  Row,
  TableColumnType,
  message,
  Select,
  Table,
  Tag,
  Modal,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  ResourcePosition,
  ResourcePositionStatus,
  getResourcePositionPageList,
  upResourcePosition,
  downResourcePosition,
} from '@/api/resourcePosition';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const ResourcePositionPage = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [form] = Form.useForm<{
    status: ResourcePositionStatus;
  }>();

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = isMobile ? 10 : 20;

  const {
    data,
    isLoading,
    refetch: refetchResourcePositionList,
  } = useQuery({
    queryKey: ['resource-position-list', currentPage],
    queryFn: async () => {
      const values = form.getFieldsValue();
      const params = {
        currentPage,
        pageSize,
        ...values,
      };
      return getResourcePositionPageList(params);
    },
  });

  const handleSearch = () => {
    setCurrentPage(1);
    refetchResourcePositionList();
  };

  const handleReset = () => {
    form.resetFields();
    setCurrentPage(1);
    refetchResourcePositionList();
  };

  const handleStatusChange = async (code: string, status: ResourcePositionStatus) => {
    const isOnline = status === ResourcePositionStatus.Online;
    const title = isOnline ? '下架确认' : '上架确认';
    const content = isOnline
      ? '下架后，该资源位下的所有素材将不会在前端展示，确认下架吗？'
      : '上架后，该资源位下已上线的素材将会在前端展示，确认上架吗？';

    Modal.confirm({
      title,
      content,
      onOk: async () => {
        try {
          if (isOnline) {
            await downResourcePosition(code);
            messageApi.success('下架成功');
          } else {
            await upResourcePosition(code);
            messageApi.success('上架成功');
          }
          refetchResourcePositionList();
        } catch (error) {
          messageApi.error(isOnline ? '下架失败' : '上架失败');
        }
      },
    });
  };

  const columns = useMemo<TableColumnType<ResourcePosition>[]>(
    () => [
      {
        title: '资源位ID',
        dataIndex: 'code',
        key: 'code',
      },
      {
        title: '位置',
        dataIndex: 'codeName',
        key: 'codeName',
      },{
        title: '描述',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        render: (status: ResourcePositionStatus) => (
          <Tag color={status === ResourcePositionStatus.Online ? 'success' : 'default'}>
            {status === ResourcePositionStatus.Online ? '上架' : '下架'}
          </Tag>
        ),
      },
      {
        title: '上线中的素材数',
        dataIndex: 'count',
        key: 'count',
      },
      {
        title: '最近更新时间',
        dataIndex: 'updateTime',
        key: 'updateTime',
        render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: '最近更新人',
        dataIndex: 'updateUserName',
        key: 'updateUserName',
      },
      {
        title: '操作',
        key: 'action',
        fixed: isMobile ? 'right' : undefined,
        width: isMobile ? 100 : undefined,
        render: (_, record) => (
          <Row gutter={[8, 8]} style={{ flexWrap: 'nowrap' }}>
            <Col>
              <Button
                type="link"
                style={{ padding: isMobile ? '4px 8px' : undefined }}
                onClick={() => {
                  navigate(`/resource-material?code=${record.code}`);
                }}
              >
                详情
              </Button>
            </Col>
            <Col>
              <Button
                type="link"
                style={{ padding: isMobile ? '4px 8px' : undefined }}
                onClick={() => handleStatusChange(record.code, record.status)}
              >
                {record.status === ResourcePositionStatus.Online ? '下架' : '上架'}
              </Button>
            </Col>
          </Row>
        ),
      },
    ],
    []
  );

  return (
    <div className="resource-position-page" style={{
      width: '100%',
      overflow: 'hidden',
      padding: isMobile ? '8px' : '16px'
    }}>
      {contextHolder}
      {/* <div className="search-form" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="status" label="状态">
            <Select
              placeholder="请选择状态"
              style={{ width: 120 }}
              allowClear
              options={[
                { label: '上线', value: ResourcePositionStatus.Online },
                { label: '下线', value: ResourcePositionStatus.Offline },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={handleReset}>重置</Button>
          </Form.Item>
        </Form>
      </div> */}

      <div style={{
        width: '100%',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        <Table
          columns={columns}
          dataSource={data?.data.records}
          loading={isLoading}
          rowKey="code"
          pagination={{
            current: currentPage,
            pageSize,
            total: data?.data.total,
            onChange: setCurrentPage,
            size: isMobile ? 'small' : 'default',
          }}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 1000 : undefined }}
          style={{
            whiteSpace: isMobile ? 'nowrap' : 'normal',
            padding: isMobile ? '8px 4px' : undefined
          }}
        />
      </div>
    </div>
  );
};

export default ResourcePositionPage;
